import { useGroupContext } from "groopit-js";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { AssignmentFilter } from "./AssignmentFilter";
import { GroopitFilterDisplayDTO } from "groopit-js/client";
import { FilterContext, useFilterContext } from "../State/FilterContext";
import {
  FilterDispatchContext,
  useFilterDispatchContext,
} from "../State/FilterDispatchContext";
import {
  isDefaultFilter,
  isLegacyFilter,
  UnnamedGroopitFilterDisplayDTO,
  updateGroopitFilter,
} from "../GroopitFilterDTO";
import equal from "fast-deep-equal";
import {
  createFilterReplaceFilterAction,
  createFilterReplaceKeywordQueryAction,
  createFilterResetFilterAction,
} from "../State/FilterActions";
import { FilterReducer } from "../State/FilterReducer";
import { StaleAssignmentFilter } from "./StaleAssignmentFilter";
import { useUpdateEffect } from "react-use";
import { SearchInput } from "../SearchInput";
import { Button } from "@material-ui/core";

interface AssignmentsFilterProps {
  onClose: () => void;
}

export const AssignmentsFilter = (props: AssignmentsFilterProps) => {
  const { onClose } = props;
  const { group } = useGroupContext();
  const filterContext = useFilterContext();
  const filterDispatchContext = useFilterDispatchContext();
  const [workingFilter, workingFilterDispatcher] = useReducer(
    FilterReducer,
    new UnnamedGroopitFilterDisplayDTO(),
  );

  useUpdateEffect(() => {
    // Per spec, if the filter changes we return you back to "display" mode,
    // not edit mode
    onClose();
  }, [filterContext.filter]);

  useEffect(() => {
    // Whenever the filter changes, update our working filter
    //
    // Legacy filters aren't editable so if you attempt to enter
    // edit mode, you start working from a new filter.
    workingFilterDispatcher(
      createFilterReplaceFilterAction(
        new UnnamedGroopitFilterDisplayDTO({
          filter: isLegacyFilter(filterContext.filter)
            ? new GroopitFilterDisplayDTO()
            : GroopitFilterDisplayDTO.fromJS(
                filterContext.filter.filter.toJSON(),
              ),
        }),
      ),
    );
  }, [filterContext.filter]);

  const hasFilterChanged = useMemo(() => {
    // This is a little tricky because creating a new instance of
    // an API object and deserializing it from JSON will lead to
    // different handling for undefined properties and cause
    // equality to fail so we intentionally serialize-deserialize
    // to ensure that the behavior matches
    return !equal(
      GroopitFilterDisplayDTO.fromJS(filterContext.filter.filter.toJSON()),
      GroopitFilterDisplayDTO.fromJS(workingFilter.filter.toJSON()),
    );
  }, [filterContext.filter, workingFilter]);

  const getFilterActions = useCallback(() => {
    return (
      <div className="flex flex-row-reverse px-2 py-3 gap-2">
        <Button
          color="primary"
          variant="contained"
          disabled={!hasFilterChanged}
          onClick={() => {
            filterDispatchContext.filterDispatcher(
              createFilterReplaceFilterAction(
                updateGroopitFilter(
                  filterContext.filter,
                  workingFilter.filter,
                  true,
                ),
              ),
            );
            onClose();
          }}
        >
          Apply
        </Button>
        <Button
          color="default"
          variant="outlined"
          disabled={isDefaultFilter(workingFilter)}
          onClick={() => {
            workingFilterDispatcher(createFilterResetFilterAction());
          }}
        >
          Clear all
        </Button>
        <Button
          color="default"
          variant="outlined"
          onClick={() => {
            workingFilterDispatcher(
              createFilterReplaceFilterAction(
                new UnnamedGroopitFilterDisplayDTO({
                  filter: new GroopitFilterDisplayDTO({
                    ...filterContext.filter.filter,
                  }),
                }),
              ),
            );
            onClose();
          }}
        >
          Cancel
        </Button>
      </div>
    );
  }, [
    filterContext.filter,
    filterDispatchContext,
    hasFilterChanged,
    onClose,
    workingFilter,
  ]);

  return (
    <FilterContext.Provider value={{ ...filterContext, filter: workingFilter }}>
      <FilterDispatchContext.Provider
        value={{
          ...filterDispatchContext,
          filterDispatcher: workingFilterDispatcher,
        }}
      >
        <div className="max-h-full lg:max-h-screen-1/2 xl:max-h-full overflow-y-auto">
          <div className="border-b-8 lg:border-b border-app-gray-08 p-0 lg:p-4">
            <SearchInput
              values={workingFilter.filter.query}
              onChange={(value) =>
                workingFilterDispatcher(
                  createFilterReplaceKeywordQueryAction(value),
                )
              }
            />
          </div>
          <div className="hidden lg:block border-b border-app-gray-08">
            {getFilterActions()}
          </div>
          <div className="border-b border-app-gray-08">
            <div className="space-y-2 py-2">
              {group.assignments?.map((assignment, i) => (
                <Fragment key={assignment.id}>
                  {i !== 0 && <hr className="border-app-gray-08" />}
                  <AssignmentFilter
                    key={assignment.id}
                    assignment={assignment}
                  />
                </Fragment>
              ))}
              {workingFilter.filter.assignments
                .filter((af) => !group.assignments?.find((a) => a.id === af.id))
                .map((af) => (
                  <Fragment key={af.id}>
                    <hr className="border-app-gray-08" />
                    <StaleAssignmentFilter key={af.id} assignmentFilter={af} />
                  </Fragment>
                ))}
            </div>
          </div>
          {getFilterActions()}
        </div>
      </FilterDispatchContext.Provider>
    </FilterContext.Provider>
  );
};
